import { useState } from "react";
import { Input } from "@/components/catalyst/Input";

const MASKED_DISPLAY = "••••••••";

interface SecretInputProps {
	value: string;
	onChange: (value: string) => void;
	hasExistingSecret: boolean;
	placeholder?: string;
	className?: string;
}

/**
 * Input for encrypted secret fields.
 * - Shows masked placeholder when blurred if value exists on server
 * - Clears on focus to allow new entry
 * - Hides input while typing (password type when focused)
 */
export function SecretInput({
	value,
	onChange,
	hasExistingSecret,
	placeholder,
	...props
}: SecretInputProps) {
	const [isFocused, setIsFocused] = useState(false);

	// Determine display value
	const displayValue = (() => {
		if (isFocused) {
			// When focused, show actual value (user is typing)
			return value;
		}
		if (value) {
			// User has entered a new value, show masked
			return MASKED_DISPLAY;
		}
		if (hasExistingSecret) {
			// Has server-side encrypted value, show masked placeholder
			return MASKED_DISPLAY;
		}
		// No value at all
		return "";
	})();

	const handleFocus = () => {
		setIsFocused(true);
	};

	const handleBlur = () => {
		setIsFocused(false);
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value);
	};

	return (
		<Input
			{...props}
			type={isFocused ? "password" : "text"}
			value={displayValue}
			onChange={handleChange}
			onFocus={handleFocus}
			onBlur={handleBlur}
			placeholder={
				isFocused ? placeholder : hasExistingSecret ? "" : placeholder
			}
		/>
	);
}
